import { Button, PreviewPanel, Skeleton } from "@design";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError, type CollectionFileSummary, getDataSourceOptions, listSpaceKnowledgeOptions } from "@/api";
import { PreviewMessage } from "@/features/files/FilePreviewContent";
import { FilePreviewPanel, usePanelLabels } from "@/features/files/FilePreviewPanel";
import { dataSourceFile, type KnowledgeFile, spaceFile } from "@/features/files/filePreview";
import { useViewport } from "@/responsive/viewport";
import type { SourceFile } from "./messageSources";

interface PanelProps {
  file: SourceFile;
  onOpen: (file: SourceFile) => void;
  onClose: () => void;
}

/**
 * A file an answer used, beside the chat (MHS-1040), in the panel the space's Files page and a data source's Content
 * tab open files in: a space's own file comes from the space, any other from its data source. Their own access checks
 * decide whether the reader sees it, so someone the chat was shared with may not.
 */
export function SourceFilePanel(props: Readonly<PanelProps>) {
  const { spaceUuid } = props.file;
  return spaceUuid ? <SpaceFile {...props} spaceUuid={spaceUuid} /> : <DataSourceFile {...props} />;
}

function SpaceFile({ spaceUuid, ...props }: Readonly<PanelProps & { spaceUuid: string }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.knowledge" });
  const files = useQuery({ ...listSpaceKnowledgeOptions({ path: { space_uuid: spaceUuid } }), select: (r) => r.data });
  // Not a member: the answer may have read it through a grant on its data source (an agent's or a project's).
  if (files.error instanceof ApiError && files.error.status === 403) return <DataSourceFile {...props} />;
  return <ShownFile {...props} files={files} listLabel={t("previewList")} fileFor={(f) => spaceFile(spaceUuid, f)} />;
}

function DataSourceFile(props: Readonly<PanelProps>) {
  const { collectionUuid } = props.file;
  const { t } = useTranslation("dataSources");
  const files = useQuery({
    ...getDataSourceOptions({ path: { collection_uuid: collectionUuid } }),
    select: (r) => r.data.files ?? [],
  });
  return (
    <ShownFile
      {...props}
      files={files}
      listLabel={t("previewList")}
      fileFor={(f) => dataSourceFile(collectionUuid, f)}
    />
  );
}

function ShownFile({
  file,
  files,
  listLabel,
  fileFor,
  onOpen,
  onClose,
}: Readonly<
  PanelProps & {
    files: UseQueryResult<CollectionFileSummary[], unknown>;
    listLabel: string;
    fileFor: (file: CollectionFileSummary) => KnowledgeFile;
  }
>) {
  const list = files.data ?? [];
  if (!list.some((f) => f.uuid === file.uuid)) {
    return (
      <Unavailable
        file={file}
        pending={files.isPending}
        error={files.error}
        onRetry={() => void files.refetch()}
        listLabel={listLabel}
        onClose={onClose}
      />
    );
  }
  return (
    <FilePreviewPanel
      files={list}
      fileUuid={file.uuid}
      fileFor={fileFor}
      listLabel={listLabel}
      onOpen={(uuid) => onOpen({ ...file, uuid, name: list.find((f) => f.uuid === uuid)?.filename ?? file.name })}
      onClose={onClose}
    />
  );
}

/** Why the file can't be shown: no access (403), gone (404, or no longer in its space or data source), or failed. */
function problemOf(error: unknown): "noAccess" | "gone" | "openFailed" {
  if (error instanceof ApiError && error.status === 403) return "noAccess";
  if (!error || (error instanceof ApiError && error.status === 404)) return "gone";
  return "openFailed";
}

/** The panel, named by the source, while the files load or when the file can't be shown: one file, one view. */
function Unavailable({
  file,
  pending,
  error,
  onRetry,
  listLabel,
  onClose,
}: Readonly<{
  file: SourceFile;
  pending: boolean;
  error: unknown;
  onRetry: () => void;
  listLabel: string;
  onClose: () => void;
}>) {
  const { t } = useTranslation("common", { keyPrefix: "filePreview" });
  const { t: tSection } = useTranslation("common", { keyPrefix: "section" });
  const labels = usePanelLabels(listLabel);
  const problem = problemOf(error);
  const { isMobile } = useViewport();
  return (
    <PreviewPanel
      defaultWidth={560}
      maxWidth={760}
      defaultExpanded={isMobile}
      labels={labels}
      artifacts={[{ key: file.uuid, name: file.name }]}
      activeIndex={0}
      onSelectArtifact={() => undefined}
      tabs={[{ value: "preview", label: t("tabs.preview") }]}
      activeTab="preview"
      onTabChange={() => undefined}
      onClose={onClose}
    >
      {pending ? (
        <Skeleton className="h-64 w-full rounded-md" />
      ) : (
        <div className="space-y-3">
          <PreviewMessage message={t(problem)} />
          {problem === "openFailed" && (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              {tSection("retry")}
            </Button>
          )}
        </div>
      )}
    </PreviewPanel>
  );
}
